import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCreateTest } from '../hooks/useTests';
import { usePacks } from '../hooks/usePacks';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Card from '../components/Card';

export default function NewTest() {
  const navigate = useNavigate();
  const location = useLocation();
  const createTest = useCreateTest();
  const { data: packsData } = usePacks({ limit: 100 });

  const [formData, setFormData] = useState({
    pack_id: (location.state as any)?.packId || '',
    test_date: new Date().toISOString().split('T')[0],
    test_operator: '',
    c_capacity: '',
    d_capacity: '',
    charge_cutoff_voltage: '',
    discharge_cutoff_voltage: '',
    soc: '',
    cycle: '',
    pack_type: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const packOptions = [
    { value: '', label: 'Select a pack...' },
    ...(packsData?.items || []).map((pack) => ({
      value: pack.pack_id,
      label: `${pack.pack_id}${pack.batch_id ? ` (${pack.batch_id})` : ''}`,
    })),
  ];

  const cycleOptions = [
    { value: '', label: 'Select cycle...' },
    { value: '1st Run', label: '1st Run' },
    { value: '2nd Run', label: '2nd Run' },
    { value: '3rd Run', label: '3rd Run' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!formData.pack_id) {
      setErrors({ pack_id: 'Pack ID is required' });
      return;
    }

    try {
      await createTest.mutateAsync({
        pack_id: formData.pack_id,
        test_date: formData.test_date ? `${formData.test_date}T00:00:00.000Z` : undefined,
        test_operator: formData.test_operator || undefined,
        c_capacity: formData.c_capacity || undefined,
        d_capacity: formData.d_capacity || undefined,
        charge_cutoff_voltage: formData.charge_cutoff_voltage || undefined,
        discharge_cutoff_voltage: formData.discharge_cutoff_voltage || undefined,
        soc: formData.soc || undefined,
        cycle: formData.cycle || undefined,
        pack_type: formData.pack_type || undefined,
      });
      navigate(`/packs/${formData.pack_id}`);
    } catch (error) {
      setErrors({ submit: 'Failed to create test. Please try again.' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Back"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            New Test
          </h1>
          <p className="text-base md:text-lg text-gray-600">
            Record a new test for a battery pack
          </p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Select
            label="Pack ID *"
            options={packOptions}
            value={formData.pack_id}
            onChange={(e) => setFormData({ ...formData, pack_id: e.target.value })}
            error={errors.pack_id}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Test Date"
              type="date"
              value={formData.test_date}
              onChange={(e) => setFormData({ ...formData, test_date: e.target.value })}
            />

            <Input
              label="Test Operator"
              value={formData.test_operator}
              onChange={(e) => setFormData({ ...formData, test_operator: e.target.value })}
              placeholder="Operator name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Cycle"
              options={cycleOptions}
              value={formData.cycle}
              onChange={(e) => setFormData({ ...formData, cycle: e.target.value })}
            />

            <Input
              label="Pack Type"
              value={formData.pack_type}
              onChange={(e) => setFormData({ ...formData, pack_type: e.target.value })}
              placeholder="e.g., Fresh Pack"
            />
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Measurements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Charge Capacity"
                type="text"
                value={formData.c_capacity}
                onChange={(e) => setFormData({ ...formData, c_capacity: e.target.value })}
                placeholder="Charge capacity"
              />

              <Input
                label="Discharge Capacity"
                type="text"
                value={formData.d_capacity}
                onChange={(e) => setFormData({ ...formData, d_capacity: e.target.value })}
                placeholder="Discharge capacity"
              />

              <Input
                label="Charge Cutoff Voltage"
                type="text"
                value={formData.charge_cutoff_voltage}
                onChange={(e) => setFormData({ ...formData, charge_cutoff_voltage: e.target.value })}
                placeholder="Voltage"
              />

              <Input
                label="Discharge Cutoff Voltage"
                type="text"
                value={formData.discharge_cutoff_voltage}
                onChange={(e) => setFormData({ ...formData, discharge_cutoff_voltage: e.target.value })}
                placeholder="Voltage"
              />

              <Input
                label="State of Charge (SOC)"
                type="text"
                value={formData.soc}
                onChange={(e) => setFormData({ ...formData, soc: e.target.value })}
                placeholder="Percentage"
              />
            </div>
          </div>

          {errors.submit && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-red-900 mb-1">Error</h4>
                  <p className="text-sm text-red-700">{errors.submit}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-4 pt-4 border-t border-gray-200">
            <Button
              type="submit"
              disabled={createTest.isPending}
              fullWidth
              className="md:w-auto"
            >
              {createTest.isPending ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </span>
              ) : (
                'Create Test'
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

